import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, DataTable, PrimaryButton, GhostButton, Input } from "@/components/Primitives";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import MaintenancePanel from "@/components/MaintenancePanel";

const INTERNAL_ROLES = ["owner", "admin", "tender_analyst", "opportunity_manager", "liaison"];

function roleLabel(r) {
  return (r || "").replace(/_/g, " ");
}

export default function Settings() {
  const { user, refresh } = useAuth();
  const isManager = ["owner", "admin"].includes(user?.role);
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [tempResult, setTempResult] = useState(null); // {email, temp_password}
  const [form, setForm] = useState({ email: "", name: "", role: "tender_analyst" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });

  const loadUsers = useCallback(() => {
    if (isManager) api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [isManager]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const createUser = async () => {
    if (!form.email || !form.name) { toast.error("Email and name are required"); return; }
    try {
      const { data } = await api.post("/auth/users", form);
      if (data.created) {
        setTempResult({ email: data.user.email, temp_password: data.temp_password });
        toast.success("User created");
      } else {
        toast.warning("User already exists — name/role updated, password unchanged. Use \"Reset PW\" to issue a new temporary password.");
      }
      setShowCreate(false);
      setForm({ email: "", name: "", role: "tender_analyst" });
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create user");
    }
  };

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/auth/users/${u.id}`, { role });
      toast.success(`${u.name} → ${roleLabel(role)}`);
      loadUsers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Role update failed"); }
  };

  const toggleActive = async (u) => {
    const next = !(u.active !== false);
    try {
      await api.patch(`/auth/users/${u.id}`, { active: next });
      toast.success(next ? "User activated" : "User deactivated");
      loadUsers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const resetPw = async (u) => {
    try {
      const { data } = await api.post(`/auth/users/${u.id}/reset-password`);
      setTempResult({ email: u.email, temp_password: data.temp_password });
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
  };

  const changeOwnPassword = async () => {
    if (pw.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "" });
      refresh && refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Password change failed"); }
  };

  return (
    <div>
      <PageHeader overline="Workspace" title="Settings" sub="Manage your +++ workspace, roles and team access." />
      <div className="p-8 space-y-6">
        {user?.must_change_password && (
          <div data-testid="must-change-pw-banner" className="border border-[#FFB300]/40 bg-[#FFB300]/[0.06] text-amber-200 text-xs px-4 py-3">
            Your account is using a temporary password. Please set a new password below.
          </div>
        )}

        <Card className="p-5">
          <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-3">Workspace</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <Meta label="Your Role" value={roleLabel(user?.role)} />
            <Meta label="Email" value={user?.email} />
            <Meta label="Name" value={user?.name} />
            <Meta label="Multi-tenant" value="workspace_id scoped" />
          </div>
        </Card>

        {/* Change your own password */}
        <Card className="p-5">
          <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-3">Your Security · Change Password</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end max-w-2xl">
            <div>
              <div className="text-zinc-500 text-[10px] uppercase tracking-widest mb-1">Current</div>
              <Input type="password" data-testid="change-pw-current" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} />
            </div>
            <div>
              <div className="text-zinc-500 text-[10px] uppercase tracking-widest mb-1">New (min 8)</div>
              <Input type="password" data-testid="change-pw-new" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
            </div>
            <PrimaryButton data-testid="change-pw-submit" onClick={changeOwnPassword}>Update Password</PrimaryButton>
          </div>
        </Card>

        {isManager && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">User Access · Internal Team</div>
              <PrimaryButton data-testid="settings-new-user-btn" onClick={() => setShowCreate(true)}>+ New User</PrimaryButton>
            </div>
            <DataTable
              columns={[
                { key: "name", label: "Name" },
                { key: "email", label: "Email" },
                {
                  key: "role", label: "Role", render: (r) => (
                    <select
                      data-testid={`user-role-select-${r.id}`}
                      value={r.role}
                      disabled={r.role === "supplier"}
                      onChange={(e) => changeRole(r, e.target.value)}
                      className="bg-black border border-white/15 text-zinc-200 text-[11px] px-2 py-1 focus:border-[#FFB300] outline-none"
                    >
                      {(r.role === "supplier" ? ["supplier"] : INTERNAL_ROLES).map((opt) => (
                        <option key={opt} value={opt}>{roleLabel(opt)}</option>
                      ))}
                    </select>
                  ),
                },
                { key: "active", label: "Active", render: (r) => <span className={r.active === false ? "text-red-400" : "text-emerald-400"}>{r.active === false ? "No" : "Yes"}</span> },
                { key: "is_demo", label: "Demo", render: (r) => r.is_demo ? <span className="text-amber-400">Demo</span> : "—" },
                {
                  key: "_act", label: "", render: (r) => (
                    <div className="flex gap-2">
                      <button data-testid={`user-reset-pw-${r.id}`} onClick={() => resetPw(r)} className="text-[#FFB300] hover:underline text-[11px]">Reset PW</button>
                      {r.id !== user?.id && (
                        <button data-testid={`user-toggle-active-${r.id}`} onClick={() => toggleActive(r)} className="text-zinc-400 hover:text-white text-[11px]">{r.active === false ? "Activate" : "Deactivate"}</button>
                      )}
                    </div>
                  ),
                },
              ]}
              rows={users}
            />
          </div>
        )}

        {isManager && (
          <MaintenancePanel />
        )}
      </div>

      {/* Create user modal */}
      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title="Create Internal User">
          <div className="space-y-3">
            <Labeled label="Email">
              <Input data-testid="new-user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@company.com" />
            </Labeled>
            <Labeled label="Full Name">
              <Input data-testid="new-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" />
            </Labeled>
            <Labeled label="Role">
              <select data-testid="new-user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full bg-black border border-white/15 text-zinc-200 text-sm px-3 py-2 focus:border-[#FFB300] outline-none">
                {INTERNAL_ROLES.map((opt) => <option key={opt} value={opt}>{roleLabel(opt)}</option>)}
              </select>
            </Labeled>
            <div className="flex justify-end gap-2 pt-2">
              <GhostButton onClick={() => setShowCreate(false)}>Cancel</GhostButton>
              <PrimaryButton data-testid="new-user-submit" onClick={createUser}>Create User</PrimaryButton>
            </div>
          </div>
        </Modal>
      )}

      {/* One-time temp password result */}
      {tempResult && (
        <Modal onClose={() => setTempResult(null)} title="Temporary Password (shown once)">
          <div data-testid="temp-password-result" className="space-y-3 text-sm">
            <div className="text-zinc-400 text-xs">For <span className="text-white">{tempResult.email}</span>. Copy and share securely — this will not be shown again.</div>
            <div data-testid="temp-password-value" className="font-mono-data text-[#FFB300] text-base border border-[#FFB300]/30 bg-black px-3 py-2 break-all select-all">{tempResult.temp_password}</div>
            <div className="text-zinc-500 text-[11px]">The user must change this password on first login (Settings → Change Password).</div>
            <div className="flex justify-end gap-2 pt-1">
              <GhostButton data-testid="temp-password-copy" onClick={async () => {
                try { await navigator.clipboard.writeText(tempResult.temp_password); toast.success("Temporary password copied"); }
                catch { toast.message("Copy failed — select the password above and copy manually."); }
              }}>Copy</GhostButton>
              <PrimaryButton data-testid="temp-password-close" onClick={() => setTempResult(null)}>Close</PrimaryButton>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Meta({ label, value }) {
  return <div><div className="text-zinc-500 text-[10px] uppercase tracking-widest mb-1">{label}</div><div className="text-white font-mono-data">{value || "—"}</div></div>;
}

function Labeled({ label, children }) {
  return <div><div className="text-zinc-500 text-[10px] uppercase tracking-widest mb-1">{label}</div>{children}</div>;
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-[#0a0a0a] border border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="text-sm uppercase tracking-[0.2em] text-[#FFB300] mb-4">{title}</div>
        {children}
      </div>
    </div>
  );
}
